import Highcharts, {
    Chart,
    ColorType,
    Options,
    PlotSeriesPointOptions,
    Point,
    TooltipFormatterContextObject, TooltipOptions,
    XAxisOptions,
    YAxisOptions,
    YAxisPlotLinesOptions,
} from 'highcharts';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { IAxisLimits, IModuleChartXAxisValues } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { getPeriodFromTime } from '@/utils/periodUtils.ts';
import { formatTimezoneOffsetDifference } from '@/utils/timezoneUtils.ts';
import cssVariables from '@/assets/cssVariables.json';
import { DEFAULT_PERIODS_PER_DAY, DEFAULT_TIMEZONE } from '@/constants/Time.ts';
import { useChartSync } from '@/composables/powerMarkets/useChartCommunication.ts';
import { TimeFormatEnum } from '@/enums/powerMarkets/TimeFormatEnum.ts';
import { hexToRgba } from '@/utils/colorUtils.ts';

dayjs.extend(advancedFormat);

const defaultFontStyle = 'Montserrat,sans-serif';
const defaultHeaderSize = '16px';
const defaultFontSize = '10px';
const defaultLegendItemSize = '10px';
const MULTI_Y_AXIS_LCP_COLOURS = [cssVariables.lcpColors.lcpBlue, cssVariables.lcpColors.sparkOrange, cssVariables.lcpColors.lcpWatermelon, cssVariables.lcpColors.lcpSunrise];
const MULTI_Y_AXIS_GRID_OPACITY = 0.25;

export const defaultXAxisValues: IModuleChartXAxisValues = {
    values: 'time',
    verticalLine: 'period',
};

export const defaultYAxisLimits: IAxisLimits = {
    min: null,
    max: null,
    minPadding: null,
    maxPadding: null,
    title: {
        text: '',
    },
};

function getDarkLightModeColours (isDarkMode: boolean): {
    themeChartTextColour: string;
    chartLineColour: string;
    chartGridLineColour: string;
} {
    const themeChartTextColour = isDarkMode ? cssVariables.theme.dark.chart.chartTextColour : cssVariables.theme.light.chart.chartTextColour;
    const chartLineColour = isDarkMode ? cssVariables.theme.dark.chart.chartLineColour : cssVariables.theme.light.chart.chartLineColour;
    const chartGridLineColour = isDarkMode ? cssVariables.theme.dark.chart.chartGridLineColour : cssVariables.theme.light.chart.chartGridLineColour;
    return {
        themeChartTextColour,
        chartLineColour,
        chartGridLineColour,
    };
}

function setZeroPlotLineColour (yAxis: YAxisOptions, colour: string): void {
    if (!yAxis.plotLines) return;
    const zeroLine = yAxis.plotLines.find((line) => line.value === 0);
    if (zeroLine) zeroLine.color = colour;
}

const LINE_CLIP_BOTTOM_PADDING_PX = 4;

type IChartClipRect = { x: number; y: number; width: number; height: number };

type IChartWithSeriesClips = Chart & {
    clipBox?: IChartClipRect;
    sharedClips?: Record<string, { attr?: (options: IChartClipRect) => void } | undefined>;
};

function extendSeriesClipBottomForLineStroke (chart: Chart): void {
    const { clipBox, sharedClips } = chart as IChartWithSeriesClips;
    if (!clipBox || !sharedClips) return;

    const extendedClip: IChartClipRect = {
        x: clipBox.x,
        y: clipBox.y,
        width: clipBox.width,
        height: clipBox.height + LINE_CLIP_BOTTOM_PADDING_PX,
    };

    Object.keys(sharedClips).forEach((clipKey) => {
        sharedClips[clipKey]?.attr?.(extendedClip);
    });
}

function getYAxisZeroPlotLine (isDarkMode: boolean, showXLine: boolean): YAxisPlotLinesOptions {
    const { chartGridLineColour } = getDarkLightModeColours(isDarkMode);

    return {
        color: chartGridLineColour,
        width: 3,
        value: 0,
        zIndex: showXLine ? 2 : 0,
    };
}

interface IMultiYAxisColours {
    gridLineColor: string;
    labelColor: string;
}

function getMultiYAxisGridColour (axisIndex: number): IMultiYAxisColours {
    const labelColor = MULTI_Y_AXIS_LCP_COLOURS[axisIndex % MULTI_Y_AXIS_LCP_COLOURS.length];
    const gridLineColor = hexToRgba(labelColor, MULTI_Y_AXIS_GRID_OPACITY) ?? labelColor;

    return {
        gridLineColor,
        labelColor,
    };
}

function getTooltipTableRow (options: {
    label: string | number;
    value: string | number;
    color?: string | ColorType;
}): string {
    let label = options.label;

    if (options.color) {
        label = `<div class="flex-container">
                    <span class="series-color" style="background-color:${options.color}"></span>
                    <span>${options.label}</span>
                </div>`;
    }

    return `<tr><td>${label}:</td><td class="value">${options.value}</td></tr>`;
}

function getTooltipDateTimeFormat (timeFormatValue: TimeFormatEnum = TimeFormatEnum.TwentyFourHour): string {
    return timeFormatValue === TimeFormatEnum.TwelveHour ? 'Do MMMM YYYY, h:mm a' : 'Do MMMM YYYY, HH:mm';
}

function getTooltipTimeOnlyFormat (timeFormatValue: TimeFormatEnum = TimeFormatEnum.TwentyFourHour): string {
    return timeFormatValue === TimeFormatEnum.TwelveHour ? 'h:mm a' : 'HH:mm';
}

function applyDateTimeLabelFormats (
    chartOptions: Options,
    timeFormatValue: TimeFormatEnum,
    showTime = true,
): void {
    const applyToAxis = (xAxis: XAxisOptions): void => {
        if (xAxis.title?.text || !showTime) return;

        if (timeFormatValue === TimeFormatEnum.TwelveHour) {
            xAxis.dateTimeLabelFormats = {
                minute: '%l:%M %P',
                hour: '%l %P',
                day: '%e. %b',
                week: '%e. %b',
                month: '%b \'%y',
                year: '%Y',
            };
            return;
        }

        xAxis.dateTimeLabelFormats = {
            millisecond: '%H:%M:%S.%L',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '%e. %b',
            week: '%e. %b',
            month: '%b \'%y',
            year: '%Y',
        };
    };

    if (Array.isArray(chartOptions.xAxis)) {
        chartOptions.xAxis.forEach(applyToAxis);
        return;
    }

    if (chartOptions.xAxis) {
        applyToAxis(chartOptions.xAxis);
    }
}

function getTooltipWrapper (options: {
    content: string;
    contentType?: 'table' | 'string';
    header?: string | {
        onlyTime?: boolean;
        date: Dayjs;
        period?: number;
        timezoneOffset?: string;
    };
    extraText?: string;
    timeFormatValue?: TimeFormatEnum;
}): string {
    const extraText = options.extraText ? `<div class="tooltip-extra-text">${options.extraText}</div>` : '';
    const timeFormatValue = options.timeFormatValue ?? TimeFormatEnum.TwentyFourHour;

    let startString = '<div class="highcharts-custom-tooltip">';
    let endString = `${extraText}</div>`;
    let header = '';

    const headerWrapper = (content: string): string => `<div class="tooltip-header">${content}</div>`;

    if (options.contentType === 'table') {
        startString += '<table><tbody>';
        endString = `</tbody></table>${endString}`;
    }
    if (options.header && typeof options.header === 'string') {
        header = headerWrapper(options.header);
    }

    if (options.header && typeof options.header !== 'string') {
        if (options.header.onlyTime) {
            header = headerWrapper(`${options.header.date.format(getTooltipTimeOnlyFormat(timeFormatValue))}`);
        } else {
            let headerText = options.header.date.format(getTooltipDateTimeFormat(timeFormatValue));

            if (parseInt(options.header.timezoneOffset ?? '0', 10)) {
                headerText += ` (${options.header.timezoneOffset})`;
            }

            if (options.header.period) {
                headerText += `, Period: ${options.header.period}`;
            }

            header = headerWrapper(headerText);
        }
    }

    return `${startString}${header}${options.content}${endString}`;
}

export interface ISeriesDataTooltipConfig {
    xPrefix?: string;
    xSuffix?: string;
    xHideOnHover?: boolean;
    yPrefix?: string;
    ySuffix?: string;
    yHideOnHover?: boolean;
    zPrefix?: string;
    zSuffix?: string;
    zHideOnHover?: boolean;
}

function getPointExtraData (innerPoint: Highcharts.PointOptionsObject): string {
    const pointWithD = innerPoint as Highcharts.PointOptionsObject & { d?: string; options?: { d?: string } };

    if (Object.hasOwn(pointWithD, 'd') && pointWithD.d != null) {
        return `, ${pointWithD.d}`;
    }

    if (pointWithD.options && Object.hasOwn(pointWithD.options, 'd') && pointWithD.options.d != null) {
        return `, ${pointWithD.options.d}`;
    }

    return '';
}

function getHighChartsYValue (config: ISeriesDataTooltipConfig, innerPoint: Highcharts.PointOptionsObject, point: TooltipFormatterContextObject): string {
    const isTime = (config.xSuffix === '' && config.xPrefix === '') || config.xSuffix === 'Time';

    const xPrefix = config.xHideOnHover ? '' : config.xPrefix ?? '';
    const xSuffix = config.xHideOnHover ? '' : config.xSuffix ?? '';

    const yPrefix = config.yHideOnHover ? '' : config.yPrefix ?? '';
    const ySuffix = config.yHideOnHover ? '' : config.ySuffix ?? '';

    const zPrefix = config.zHideOnHover ? '' : config.zPrefix;
    const zSuffix = config.zHideOnHover ? '' : config.zSuffix;

    let yString = '';

    if (isTime) {
        if (innerPoint && Object.hasOwn(innerPoint, 'high')) {
            if (Object.hasOwn(innerPoint, 'open')) {
                yString = `O: ${Highcharts.numberFormat(+innerPoint.open!.toFixed(2), -1, '.', ',')} || H: ${Highcharts.numberFormat(+innerPoint.high!.toFixed(2), -1, '.', ',')} || L: ${Highcharts.numberFormat(+innerPoint.low!.toFixed(2), -1, '.', ',')} || C: ${Highcharts.numberFormat(+innerPoint.close!.toFixed(2), -1, '.', ',')} (${(config.yPrefix ?? '') + (config.ySuffix ?? '')})`;
            } else {
                yString = `${yPrefix}${Highcharts.numberFormat(+innerPoint.low!.toFixed(2), -1, '.', ',')}${ySuffix} || ${yPrefix}${Highcharts.numberFormat(+innerPoint.high!.toFixed(2), -1, '.', ',')}${ySuffix}`;
            }
        } else {
            yString = point.y != null ? `${yPrefix}${Highcharts.numberFormat(+point.y.toFixed(2), -1, '.', ',')}${ySuffix}` : '';
        }
    } else {
        const isCumulativeBid = point.series?.userOptions?.id?.includes('BoaVolumePriceCurveBid');
        const isCumulativeOffer = point.series?.userOptions?.id?.includes('BoaVolumePriceCurveOffer');

        let x = 0;
        if ((isCumulativeBid || isCumulativeOffer) && point.x != null) {
            const seriesXData = (point.series as Highcharts.Series & { xData?: Array<number | null> }).xData;
            const previousX = seriesXData?.[point.point.index - 1];
            x = point.point.index === 0 || previousX == null
                ? Number(point.x)
                : Number(point.x) - Number(previousX);
        } else {
            x = Number(point.x);
        }

        if (point.y != null) {
            yString = `${xPrefix}${Highcharts.numberFormat(+x.toFixed(2), -1, '.', ',')}${xSuffix}, ${yPrefix}${Highcharts.numberFormat(+point.y.toFixed(2), -1, '.', ',')}${ySuffix}`;
        }
    }

    const zString = config.zPrefix != null && innerPoint.z != null
        ? (`, ${zPrefix}${Highcharts.numberFormat(+innerPoint.z.toFixed(2), -1, '.', ',')}${zSuffix}`)
        : '';

    return `${yString}${zString}${getPointExtraData(innerPoint)}`;
}

function flipDarkLightMode (colour: 'dark' | 'blue' | 'light', chartOptions: Options, multipleFigures: boolean = false): void {
    chartOptions.credits = chartOptions.credits || {};
    chartOptions.credits.style = chartOptions.credits.style || {};
    chartOptions.legend = chartOptions.legend || {};
    chartOptions.legend.itemStyle = chartOptions.legend.itemStyle || {};
    chartOptions.exporting = chartOptions.exporting || {};
    chartOptions.exporting.chartOptions = chartOptions.exporting.chartOptions || {};
    chartOptions.exporting.chartOptions.chart = chartOptions.exporting.chartOptions.chart || {};

    if (colour === 'dark' || colour === 'blue') {
        chartOptions.credits.style.color = '#fff';
        chartOptions.legend.itemStyle.color = '#fff';
        chartOptions.exporting.chartOptions.chart.backgroundColor = '#3a3a3f';

        if (Array.isArray(chartOptions.yAxis)) {
            chartOptions.yAxis?.forEach((yAxis, index) => {
                if (index !== 0 && !multipleFigures) {
                    yAxis.lineWidth = 0;
                    yAxis.minorGridLineWidth = 0;
                    yAxis.gridLineColor = 'transparent';
                } else {
                    yAxis.gridLineColor = '#212124';
                }

                setZeroPlotLineColour(yAxis, '#212124');

                if (yAxis.labels?.style) yAxis.labels.style.color = '#fff';
                if (yAxis.title?.style) yAxis.title.style.color = '#fff';
            });
        }

        if (Array.isArray(chartOptions.xAxis)) {
            chartOptions.xAxis?.forEach(xAxis => {
                if (xAxis.labels?.style) xAxis.labels.style.color = '#fff';
                if (xAxis.title?.style) xAxis.title.style.color = '#fff';
                if ('plotBands' in xAxis && xAxis.plotBands) xAxis.plotBands[0].color = '#212124';
            });
        }
    } else {
        chartOptions.credits.style.color = '#151517';
        chartOptions.legend.itemStyle.color = '#151517';
        chartOptions.exporting.chartOptions.chart.backgroundColor = '#fefefe';

        if (Array.isArray(chartOptions.yAxis)) {
            chartOptions.yAxis?.forEach((yAxis) => {
                yAxis.gridLineColor = '#cecece';
                setZeroPlotLineColour(yAxis, '#cecece');
                if (yAxis.labels?.style) yAxis.labels.style.color = '#151517';
                if (yAxis.title?.style) yAxis.title.style.color = '#151517';
            });
        }

        if (Array.isArray(chartOptions.xAxis)) {
            chartOptions.xAxis?.forEach((xAxis) => {
                if ('plotBands' in xAxis && xAxis.plotBands) xAxis.plotBands[0].color = '#cecece';
                if (xAxis.labels?.style) xAxis.labels.style.color = '#151517';
                if (xAxis.title?.style) xAxis.title.style.color = '#151517';
            });
        }
    }
}

function getSeriesTooltipConfig (series: Highcharts.Series): ISeriesDataTooltipConfig {
    return series.options as ISeriesDataTooltipConfig;
}

function tooltipFunction (
    tooltipContext: TooltipFormatterContextObject,
    timezone: string = DEFAULT_TIMEZONE,
    periodsPerDay: number = DEFAULT_PERIODS_PER_DAY,
    referenceTimezone: string = DEFAULT_TIMEZONE,
    timeFormatValue: TimeFormatEnum = TimeFormatEnum.TwentyFourHour,
): string | null {
    if (!tooltipContext) return null;

    let time = tooltipContext.x ? dayjs(tooltipContext.x) : dayjs();

    if (timezone) {
        time = time.tz(timezone);
    }

    const period = getPeriodFromTime(time, periodsPerDay, timezone);

    if (tooltipContext.points) {
        const tooltip = tooltipContext.point.series;

        const plotHeight = tooltip.chart.plotHeight;

        const sizeOfRow = 10; // Line height of tooltip row set in base.scss

        let pointNo = (plotHeight - sizeOfRow) / (sizeOfRow * 2.5);
        if (pointNo < 1) pointNo = Math.max(1, Math.min(tooltipContext.points.length, 15));

        const extraText = tooltipContext.points.length > pointNo ? '... (view table for full list)' : '';

        let hasTime = false;

        let rows: string = '';

        const legendItems = tooltip.chart.legend?.allItems ?? [];
        const pointsInLegendOrder = [...tooltipContext.points].sort((a, b) => (
            legendItems.findIndex(item => item.name === a.series.name)
            - legendItems.findIndex(item => item.name === b.series.name)
        ));

        pointsInLegendOrder.slice(0, pointNo).forEach((point) => {
            const config = getSeriesTooltipConfig(point.series);
            const y = getHighChartsYValue(config, point.point, point);

            const isTime = (config.xSuffix === '' && config.xPrefix === '') || config.xSuffix === 'Time';
            hasTime = hasTime || isTime;
            rows += getTooltipTableRow({
                label: point.series.name,
                value: y,
                color: point.series.color,
            });
        });
        return getTooltipWrapper({
            content: rows,
            contentType: 'table',
            extraText,
            timeFormatValue,
            header: hasTime ? {
                date: time,
                period,
                timezoneOffset: ((): string | undefined => {
                    const selectedOffset = time.utcOffset();
                    const referenceOffset = dayjs().tz(referenceTimezone).utcOffset();
                    return formatTimezoneOffsetDifference(selectedOffset, referenceOffset) ?? undefined;
                })(),
            } : undefined,
        });
    }
    const config = getSeriesTooltipConfig(tooltipContext.point.series);
    const y = getHighChartsYValue(config, tooltipContext.point, tooltipContext);

    const isTime = (config.xSuffix === '' && config.xPrefix === '') || config.xSuffix === 'Time';

    return getTooltipWrapper({
        content: getTooltipTableRow({
            label: tooltipContext.point.series.name,
            color: tooltipContext.point.series.color,
            value: y,
        }),
        contentType: 'table',
        timeFormatValue,
        header: isTime ? {
            date: time,
            period,
        } : undefined,
    });
}

function getTooltip (
    timezone: string = DEFAULT_TIMEZONE,
    periodsPerDay: number = DEFAULT_PERIODS_PER_DAY,
    referenceTimezone: string = DEFAULT_TIMEZONE,
    timeFormatValue: TimeFormatEnum = TimeFormatEnum.TwentyFourHour,
): TooltipOptions {
    return {
        formatter (this: TooltipFormatterContextObject): string | null {
            return tooltipFunction(this, timezone, periodsPerDay, referenceTimezone, timeFormatValue);
        },
    };
}

function getChartOptions (isDarkMode: boolean, showXLine: boolean, timezone: string = DEFAULT_TIMEZONE): Options {
    const { themeChartTextColour, chartLineColour } = getDarkLightModeColours(isDarkMode);
    return {
        chart: {
            backgroundColor: 'transparent',
            animation: false,
            events: {
                render (): void {
                    extendSeriesClipBottomForLineStroke(this);
                },
            },
            spacingBottom: -5,
            spacingTop: 5,
            spacingRight: 0,
            spacingLeft: 0,
            style: {
                fontFamily: defaultFontStyle,
            },
            panning: {
                enabled: true,
                type: 'x',
            },
            panKey: 'shift',
            zooming: {
                type: 'x',
                resetButton: {
                    position: {
                        x: 0,
                        y: 0,
                    },
                    theme: {
                        'stroke-width': 1,
                        r: 4,
                        padding: 8,
                        style: {
                            fontSize: defaultFontSize,
                        },
                    },
                },
            },
        },
        boost: {
            // enabled: true,
            // allowForce: false,
            //     //"useGPUTranslations: true,
            //     //"usePreallocated: true,
            //     useAlpha: false
            // "seriesThreshold: 5
        },
        credits: {
            enabled: false,
            href: '',
            style: {
                color: themeChartTextColour,
                fontSize: defaultFontSize,
            },
        },
        accessibility: {
            enabled: false,
        },
        exporting: {
            enabled: false,
            buttons: {
                contextButton: {
                    theme: {
                        fill: '#00a3c7',
                    },
                    menuItems: [
                        'printChart',
                        'separator',
                        'downloadPNG',
                        'downloadJPEG',
                        'downloadPDF',
                        'downloadSVG',
                    ],
                },
            },
            chartOptions: {
                plotOptions: {
                    series: {
                        lineWidth: 3,
                    },
                },
                chart: {
                    backgroundColor: cssVariables.theme[isDarkMode ? 'dark' : 'light'].chart.chartBackground,
                },
                legend: {
                    maxHeight: 100,
                    margin: 0,
                    width: '100%',
                },
            },
        },
        time: {
            timezone,
        },
        legend: {
            borderRadius: 6,
            borderWidth: 0,
            bubbleLegend: {
                enabled: false,
                borderWidth: 1,
                connectorDistance: 40,
                ranges: [{ color: 'rgba(233, 63, 111, 0.7)' }, { color: 'rgba(247, 166, 0, 0.7)' }, { color: 'rgba(0, 163, 199, 0.7)' }],
            },
            enabled: true,
            itemStyle: {
                color: themeChartTextColour,
                fontSize: defaultLegendItemSize,
                fontWeight: 'normal',
            },
            // width: '100%',
            itemDistance: 10,
            // x: 10,
            maxHeight: 60,
            margin: 0,
        },
        plotOptions: {
            ohlc: {
                lineWidth: 2.5,
            },
            scatter: {
                marker: {
                    radius: 5,
                },
            },
            line: {
                marker: {
                    enabled: false,
                },
                clip: true,
            },
            series: {
                dataGrouping: {
                    enabled: false,
                    approximation: undefined,
                },
                showInNavigator: true,
                animation: false,
                events: {
                },
                boostThreshold: 0,
            },
            column: {
                stacking: 'normal',
                dataLabels: {
                    enabled: false,
                },
                groupPadding: 0,
                pointPadding: 0.1,
            },
            arearange: {
                // fillOpacity: 0.4,
                // opacity: 0.4
            },
            area: {
                stacking: 'normal',
                dataLabels: {
                    enabled: false,
                },
                marker: {
                    enabled: false,
                },
                trackByArea: true,
            },
            boxplot: {
                lineWidth: 3,
                whiskerWidth: undefined,
                medianWidth: null,
                fillColor: 'white',
            },
        },
        navigator: {
            enabled: false,
        },
        scrollbar: {
            enabled: false,
        },
        rangeSelector: {
            enabled: false,
        },
        series: [],
        title: {
            style: {
                color: themeChartTextColour,
                fontSize: defaultHeaderSize,
            },
            text: '',
        },
        tooltip: {
            animation: false,
            backgroundColor: undefined,
            borderRadius: 15,
            borderWidth: 0,
            followPointer: true,
            hideDelay: 0,
            style: {
                color: themeChartTextColour,
                fontSize: defaultLegendItemSize,
                zIndex: 9999,
            },
            formatter: undefined,
            split: true,
            shared: false,
            outside: true,
            useHTML: true,
        },
        navigation: {
            buttonOptions: {
                verticalAlign: 'top',
                y: -20,
            },
        },
        xAxis: [{
            crosshair: true,
            ordinal: false,
            labels: {
                style: {
                    color: themeChartTextColour,
                    fontSize: defaultFontSize,
                },
            },
            lineColor: chartLineColour,
            title: {
                align: 'middle',
                style: {
                    color: themeChartTextColour,
                    fontSize: defaultFontSize,
                },
                text: '',
            },
        }],
        yAxis: [getYAxis(isDarkMode, showXLine)],
    };
}

let leaveTimer: number | null = null;

function getSyncTooltipHover (getHoverTime: (point: Point) => number | undefined): PlotSeriesPointOptions {
    const chartSync = useChartSync();

    return {
        events: {
            mouseOver (): void {
                const point = this as Highcharts.Point;
                if (leaveTimer) {
                    clearTimeout(leaveTimer);
                    leaveTimer = null;
                }

                const hoverTime = getHoverTime(point);

                chartSync.setHoverTimestamp(hoverTime ?? null);
            },
            mouseOut (): void {
                leaveTimer = window.setTimeout(() => {
                    chartSync.setHoverTimestamp(null);
                }, 50);
            },
        },
    };
}

function getYAxis (isDarkMode: boolean, showXLine: boolean): YAxisOptions {
    const { themeChartTextColour, chartLineColour } = getDarkLightModeColours(isDarkMode);

    return {
        labels: {
            style: {
                color: themeChartTextColour,
                fontSize: defaultFontSize,
            },
        },
        lineColor: chartLineColour,
        title: {
            style: {
                color: themeChartTextColour,
                fontSize: defaultFontSize,
            },
            margin: 2,
            text: '',
        },
        stackLabels: {
            enabled: false,
        },
        plotLines: [getYAxisZeroPlotLine(isDarkMode, showXLine)],
        opposite: false,
    };
}

export {
    getTooltip,
    getHighChartsYValue,
    getTooltipWrapper,
    getTooltipTableRow,
    getChartOptions,
    flipDarkLightMode,
    getYAxis,
    getYAxisZeroPlotLine,
    getMultiYAxisGridColour,
    getSyncTooltipHover,
    applyDateTimeLabelFormats,
};
